import React, { Component } from 'react';

import configureDecoratedComponent from 'client/helpers/configureDecoratedComponent';

const CHECK_INTERVAL = 250;

export default function withInfiniteScroll(params = {}) {
  return function withInfiniteScrollDecorator(ComponentToDecorate) {
    class DecoratedComponent extends Component {
      constructor(props) {
        super();

        this.id =
          typeof params.savePositionWithId === 'function'
            ? params.savePositionWithId(props)
            : params.savePositionWithId;
      }

      componentDidMount() {
        const infiniteScrollCallback = this._infiniteScrollCallback;
        const infiniteScrollWrapperEl = this._infiniteScrollWrapperEl;

        if (typeof infiniteScrollCallback !== 'function') {
          return;
        }

        if (!infiniteScrollWrapperEl) {
          return;
        }

        this._interval = setInterval(this._checkScrolled, CHECK_INTERVAL);
      }

      componentWillUnmount() {
        clearInterval(this._interval);
        this._infiniteScrollCallback = null;
        this._infiniteScrollWrapperEl = null;
      }

      _onInfiniteScrollCallbackReady = (infiniteScrollCallback) => {
        this._infiniteScrollCallback = infiniteScrollCallback;
      };

      _onInfiniteScrollWrapperRef = (infiniteScrollWrapperEl) => {
        this._infiniteScrollWrapperEl = infiniteScrollWrapperEl;
      };

      _checkScrolled = () => {
        const infiniteScrollCallback = this._infiniteScrollCallback;
        const infiniteScrollWrapperEl = this._infiniteScrollWrapperEl;
        if (!infiniteScrollCallback || !infiniteScrollWrapperEl) {
          return;
        }

        // Only fire after the user has actually scrolled the window. Without this
        // guard the polling loop fires every CHECK_INTERVAL whenever the wrapper
        // bottom is naturally near the viewport bottom — which is the case
        // immediately after a filter change clears most rows. The callback is the
        // HOC's `_loadNextPage`, so without arming on scroll the table auto-loads
        // every page back-to-back until it's tall enough to push the wrapper bottom
        // past the viewport (or `isFullyLoaded`), instead of loading just page 1
        // and waiting for the user to scroll. Page 1 is loaded explicitly by the
        // HOC's `componentDidMount` / `componentWillReceiveProps`, so gating the
        // scroll-driven path here doesn't break first-load behavior.
        if ((global.scrollY || 0) <= 0) {
          return;
        }

        const windowHeight = global.innerHeight || global.document.documentElement.clientHeight;
        const rect = infiniteScrollWrapperEl.getBoundingClientRect();
        const offset = params.offset || 0;

        if (rect.bottom - offset <= windowHeight) {
          infiniteScrollCallback();
        }
      };

      render() {
        return (
          <ComponentToDecorate
            {...this.props}
            onInfiniteScrollCallbackReady={this._onInfiniteScrollCallbackReady}
            onInfiniteScrollWrapperRef={this._onInfiniteScrollWrapperRef}
          />
        );
      }
    }

    configureDecoratedComponent({
      DecoratedComponent: DecoratedComponent,
      OriginalComponent: ComponentToDecorate,
      displayNameSuffix: 'InfiniteScroll',
    });

    return DecoratedComponent;
  };
}
